import React from 'react';
import { useTranslation } from 'react-i18next';
import type { Selection } from 'react-aria-components';
import { Icon } from '@iconify/react';
import { Button, Dropdown, Label, ListBox, Pagination } from '@heroui/react';
import { DataGrid, InlineSelect, NumberValue, Widget } from '@heroui-pro/react';
import type { DataGridColumn } from '@heroui-pro/react';
import { formatGel } from '@/utils/money';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';
import {
    CATEGORIES_PAGE_SIZE_OPTIONS,
    CATEGORY_COLUMN,
    CATEGORY_ROW_ACTIONS,
    NO_IMAGE_ICON,
    type Category,
    type CategoryRowAction,
    formatMarkupPercent,
} from '../categoriesMeta';

type CategoryTableProps = {
    data: Category[];
    expandedKeys: Selection;
    onDeleteCategory: (id: string) => void;
    onEditCategory: (id: string) => void;
    onExpandedChange: (keys: Selection) => void;
    onManageAttributes: (id: string) => void;
    onPageChange: (page: number) => void;
    onPageSizeChange: (pageSize: number) => void;
    page: number;
    pageSize: number;
    pageWindow: (number | 'ellipsis')[];
    totalCount: number;
    totalPages: number;
};

/**
 * Render-only categories grid — an expandable tree (`getChildren` → `children`,
 * `treeColumn` on name). The top level is paginated client-side (`data` is
 * already the current page of root rows, each with its full subtree); the
 * footer drives the page / page-size controls. Columns aren't sortable — the
 * tree comes name-ordered from the API and sorting would only reorder one page.
 */
export const CategoryTable: React.FC<CategoryTableProps> = ({
    data,
    expandedKeys,
    onDeleteCategory,
    onEditCategory,
    onExpandedChange,
    onManageAttributes,
    onPageChange,
    onPageSizeChange,
    page,
    pageSize,
    pageWindow,
    totalCount,
    totalPages,
}) => {
    const { t } = useTranslation('pages');

    const copy = React.useMemo(
        () => ({
            tableAria: t('categories.components.categoryTable.tableAria'),
            actionsAria: t('categories.components.categoryTable.actionsAria'),
            previous: t(
                'categories.components.categoryTable.pagination.previous',
            ),
            next: t('categories.components.categoryTable.pagination.next'),
            perPage: t(
                'categories.components.categoryTable.pagination.perPage',
            ),
            columns: {
                image: t('categories.components.categoryTable.columns.image'),
                name: t('categories.components.categoryTable.columns.name'),
                slug: t('categories.components.categoryTable.columns.slug'),
                products: t(
                    'categories.components.categoryTable.columns.products',
                ),
                totalCost: t(
                    'categories.components.categoryTable.columns.totalCost',
                ),
                totalSalePrice: t(
                    'categories.components.categoryTable.columns.totalSalePrice',
                ),
                totalMarkup: t(
                    'categories.components.categoryTable.columns.totalMarkup',
                ),
            },
            empty: {
                title: t('categories.components.categoryTable.empty.title'),
            },
        }),
        [t],
    );

    const onRowAction = React.useCallback(
        (id: string, action: CategoryRowAction) => {
            if (action === 'manageAttributes') {
                onManageAttributes(id);
                return;
            }
            if (action === 'edit') {
                onEditCategory(id);
                return;
            }
            onDeleteCategory(id);
        },
        [onDeleteCategory, onEditCategory, onManageAttributes],
    );

    const columns = React.useMemo<DataGridColumn<Category>[]>(
        () => [
            {
                id: CATEGORY_COLUMN.image,
                header: copy.columns.image,
                width: 72,
                cell: (item) =>
                    item.imageUrl ? (
                        <img
                            alt={item.name}
                            className="size-10 rounded-[8px] object-cover"
                            src={item.imageUrl}
                            {...getNodeTestIdProp(
                                `category-row-image-${item.id}`,
                            )}
                        />
                    ) : (
                        <div
                            aria-hidden
                            className="flex size-10 items-center justify-center rounded-[8px] bg-surface-secondary text-muted"
                            {...getNodeTestIdProp(
                                `category-row-image-fallback-${item.id}`,
                            )}
                        >
                            <Icon className="size-5" icon={NO_IMAGE_ICON} />
                        </div>
                    ),
            },
            {
                id: CATEGORY_COLUMN.name,
                header: copy.columns.name,
                accessorKey: 'name',
                isRowHeader: true,
                cell: (item) => (
                    <span
                        className="text-sm font-medium text-foreground"
                        {...getNodeTestIdProp(`category-row-name-${item.id}`)}
                    >
                        {item.name}
                    </span>
                ),
            },
            {
                id: CATEGORY_COLUMN.slug,
                header: copy.columns.slug,
                accessorKey: 'slug',
                cell: (item) => (
                    <span
                        className="font-mono text-xs text-muted"
                        {...getNodeTestIdProp(`category-row-slug-${item.id}`)}
                    >
                        {item.slug}
                    </span>
                ),
            },
            {
                id: CATEGORY_COLUMN.products,
                header: copy.columns.products,
                accessorKey: 'productCount',
                width: 110,
                align: 'end',
                cell: (item) => (
                    <NumberValue
                        className="text-sm text-foreground tabular-nums"
                        style="decimal"
                        value={item.productCount}
                        {...getNodeTestIdProp(
                            `category-row-products-${item.id}`,
                        )}
                    />
                ),
            },
            {
                id: CATEGORY_COLUMN.totalCost,
                header: copy.columns.totalCost,
                accessorKey: 'totalCostTetri',
                width: 130,
                align: 'end',
                // BOS-162: money, and now labelled as such. It rendered as a
                // bare `0.00` with no unit at all, which in a row beside a
                // percentage column is a number of nothing.
                cell: (item) => (
                    <span
                        className="text-sm tabular-nums text-foreground"
                        {...getNodeTestIdProp(
                            `category-row-total-cost-${item.id}`,
                        )}
                    >
                        {formatGel(item.totalCostTetri)}
                    </span>
                ),
            },
            {
                id: CATEGORY_COLUMN.totalSalePrice,
                header: copy.columns.totalSalePrice,
                accessorKey: 'totalSalePriceTetri',
                width: 140,
                align: 'end',
                cell: (item) => (
                    <span
                        className="text-sm tabular-nums text-foreground"
                        {...getNodeTestIdProp(
                            `category-row-total-sale-price-${item.id}`,
                        )}
                    >
                        {formatGel(item.totalSalePriceTetri)}
                    </span>
                ),
            },
            {
                id: CATEGORY_COLUMN.totalMarkup,
                header: copy.columns.totalMarkup,
                accessorKey: 'totalMarkup',
                width: 130,
                align: 'end',
                // A percentage, not money: the API returns markup over cost as
                // `108.3`, and rendering it beside two currency columns as
                // "108.30" read as 108 GEL (BOS-137).
                cell: (item) => (
                    <span
                        className="text-sm font-medium tabular-nums text-success-soft-foreground"
                        {...getNodeTestIdProp(
                            `category-row-total-markup-${item.id}`,
                        )}
                    >
                        {formatMarkupPercent(item.totalMarkup)}
                    </span>
                ),
            },
            {
                id: CATEGORY_COLUMN.actions,
                header: '',
                pinned: 'end',
                width: 40,
                align: 'center',
                // The pinned cell is sticky over the horizontally scrolling
                // body; without an explicit (opaque) background it stays
                // transparent and the cells underneath bleed through. Match the
                // sibling body/header surfaces so it reads as one column.
                cellClassName: 'bg-surface',
                headerClassName: 'bg-surface-secondary',
                cell: (item) => (
                    <div
                        className="flex justify-center"
                        {...getNodeTestIdProp(
                            `category-row-actions-${item.id}`,
                        )}
                    >
                        <Dropdown>
                            <Button
                                aria-label={copy.actionsAria}
                                isIconOnly
                                size="sm"
                                variant="tertiary"
                                {...getNodeTestIdProp(
                                    `category-row-actions-trigger-${item.id}`,
                                )}
                            >
                                <Icon icon="gravity-ui:ellipsis-vertical" />
                            </Button>
                            <Dropdown.Popover className="min-w-[160px]">
                                <Dropdown.Menu
                                    onAction={(key) =>
                                        onRowAction(
                                            item.id,
                                            key as CategoryRowAction,
                                        )
                                    }
                                >
                                    {CATEGORY_ROW_ACTIONS.map((action) => (
                                        <Dropdown.Item
                                            id={action.id}
                                            key={action.id}
                                            textValue={t(action.labelKey)}
                                            variant={action.variant}
                                            {...getNodeTestIdProp(
                                                `category-row-action-${action.id}-${item.id}`,
                                            )}
                                        >
                                            <Icon icon={action.icon} />
                                            <Label>{t(action.labelKey)}</Label>
                                        </Dropdown.Item>
                                    ))}
                                </Dropdown.Menu>
                            </Dropdown.Popover>
                        </Dropdown>
                    </div>
                ),
            },
        ],
        [copy, onRowAction, t],
    );

    return (
        <Widget
            className="flex flex-col gap-0 overflow-hidden p-0"
            {...getNodeTestIdProp('category-table-widget')}
        >
            <DataGrid<Category>
                aria-label={copy.tableAria}
                columns={columns}
                data={data}
                expandedKeys={expandedKeys}
                getChildren={(item) => item.children}
                getRowId={(item) => item.id}
                onExpandedChange={onExpandedChange}
                treeColumn={CATEGORY_COLUMN.name}
                renderEmptyState={() => (
                    <div
                        className="flex flex-col items-center gap-3 py-12 text-muted"
                        {...getNodeTestIdProp('category-table-empty')}
                    >
                        <Icon
                            aria-hidden
                            className="size-8 text-default-500"
                            icon="gravity-ui:folder"
                        />
                        <span
                            className="text-sm"
                            {...getNodeTestIdProp('category-table-empty-title')}
                        >
                            {copy.empty.title}
                        </span>
                    </div>
                )}
                variant="primary"
                {...getNodeTestIdProp('category-table-grid')}
            />
            {totalCount > CATEGORIES_PAGE_SIZE_OPTIONS[0]! ? (
                <div
                    className="flex flex-wrap items-center justify-between gap-3 border-t border-separator p-3"
                    {...getNodeTestIdProp('category-table-pagination')}
                >
                    <div className="flex items-center gap-2 text-xs text-muted">
                        <span>{copy.perPage}</span>
                        <InlineSelect
                            aria-label={copy.perPage}
                            onChange={(value) =>
                                value && onPageSizeChange(Number(value))
                            }
                            value={String(pageSize)}
                            {...getNodeTestIdProp('category-table-per-page')}
                        >
                            <InlineSelect.Trigger>
                                <InlineSelect.Value />
                                <InlineSelect.Indicator />
                            </InlineSelect.Trigger>
                            <InlineSelect.Popover className="w-[96px]">
                                <ListBox>
                                    {CATEGORIES_PAGE_SIZE_OPTIONS.map(
                                        (size) => (
                                            <ListBox.Item
                                                id={String(size)}
                                                key={size}
                                                textValue={String(size)}
                                            >
                                                {size}
                                                <ListBox.ItemIndicator />
                                            </ListBox.Item>
                                        ),
                                    )}
                                </ListBox>
                            </InlineSelect.Popover>
                        </InlineSelect>
                    </div>
                    {totalPages > 1 ? (
                        <Pagination size="sm">
                            <Pagination.Content>
                                <Pagination.Item>
                                    <Pagination.Previous
                                        aria-label={copy.previous}
                                        isDisabled={page === 1}
                                        onPress={() => onPageChange(page - 1)}
                                    >
                                        <Pagination.PreviousIcon />
                                    </Pagination.Previous>
                                </Pagination.Item>
                                {pageWindow.map((entry, index) =>
                                    entry === 'ellipsis' ? (
                                        <Pagination.Item
                                            key={`category-table-page-ellipsis-${index}`}
                                        >
                                            <Pagination.Ellipsis />
                                        </Pagination.Item>
                                    ) : (
                                        <Pagination.Item key={entry}>
                                            <Pagination.Link
                                                isActive={entry === page}
                                                onPress={() =>
                                                    onPageChange(entry)
                                                }
                                                {...getNodeTestIdProp(
                                                    `category-table-page-${entry}`,
                                                )}
                                            >
                                                {entry}
                                            </Pagination.Link>
                                        </Pagination.Item>
                                    ),
                                )}
                                <Pagination.Item>
                                    <Pagination.Next
                                        aria-label={copy.next}
                                        isDisabled={page === totalPages}
                                        onPress={() => onPageChange(page + 1)}
                                    >
                                        <Pagination.NextIcon />
                                    </Pagination.Next>
                                </Pagination.Item>
                            </Pagination.Content>
                        </Pagination>
                    ) : null}
                </div>
            ) : null}
        </Widget>
    );
};
